import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { detectPlatform } from "../platform.js";

/**
 * Android hardware/gesture back. Priority order:
 *  1. An open modal (`role="dialog"` — @app/ui Dialog and TaskFormDialog both
 *     carry it) closes first, via a synthetic Escape keydown — every dialog
 *     already implements Escape-to-close, so back ≈ Esc.
 *  2. In-app history navigates back.
 *  3. At the history root the app MINIMIZES (never exits — Capacitor's default
 *     with no listener registered is to close the activity).
 * No-op on non-Capacitor platforms.
 */
export function useAndroidBackButton(): void {
  const navigate = useNavigate();
  useEffect(() => {
    if (detectPlatform() !== "capacitor") return;
    let removed = false;
    let remove: (() => void) | null = null;
    void import("@capacitor/app").then(({ App }) =>
      App.addListener("backButton", ({ canGoBack }) => {
        const dialog = document.querySelector('[role="dialog"]');
        if (dialog) {
          dialog.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
          return;
        }
        if (canGoBack) navigate(-1);
        else void App.minimizeApp();
      }).then((handle) => {
        if (removed) void handle.remove();
        else remove = () => void handle.remove();
      }),
    );
    return () => {
      removed = true;
      remove?.();
    };
  }, [navigate]);
}
